import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, X, ClipboardList, Filter, Calendar, TrendingUp } from "lucide-react";
import {
  inventoryListOrders, inventoryCreateOrder, inventoryUpdateOrder, inventoryDeleteOrder,
  inventoryListSuppliers, inventoryListMaterials,
  ORDER_STATUS_COLORS,
} from "../../../lib/inventory";

const parseApiError = (e, fallback = "Save failed") => {
  const detail = e?.response?.data?.detail;
  if (!detail) return e?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  if (typeof detail === "object") return detail?.msg || JSON.stringify(detail);
  return String(detail);
};

const fmtUSD = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }); } catch { return iso; }
};
const isoDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toISOString().split("T")[0]; } catch { return ""; }
};

const StatusPill = ({ status }) => {
  const c = ORDER_STATUS_COLORS[status] || ORDER_STATUS_COLORS.Placed;
  return (
    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>
      {status}
    </span>
  );
};

const OrderForm = ({ initial, suppliers, materials, onSave, onCancel, busy }) => {
  const [supplierId, setSupplierId] = useState(initial?.supplier_id || "");
  const [datePlaced, setDatePlaced] = useState(initial?.date_placed ? isoDate(initial.date_placed) : isoDate(new Date().toISOString()));
  const [total, setTotal] = useState(initial?.total_amount ?? 0);
  const [status, setStatus] = useState(initial?.status || "Placed");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [items, setItems] = useState(initial?.items?.length ? initial.items : [{ material_id: "", qty: 1 }]);

  const addLine = () => setItems((p) => [...p, { material_id: "", qty: 1 }]);
  const removeLine = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const updateLine = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  // For new orders we let supplier dropdown filter materials. For existing edits user gets fewer fields.
  const isEdit = !!initial?.id;

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleaned = items.filter((it) => it.material_id && parseInt(it.qty || 0, 10) > 0)
      .map((it) => ({ material_id: it.material_id, qty: parseInt(it.qty, 10) }));
    if (!isEdit && cleaned.length === 0) { toast.error("Add at least one line item with quantity"); return; }
    onSave({
      supplier_id: supplierId || null,
      items: cleaned,
      total_amount: parseFloat(total) || 0,
      date_placed: datePlaced,
      status,
      notes: notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit}
      className="p-4 rounded-sm grid gap-3 sm:grid-cols-4"
      style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }}
      data-testid="inventory-order-form"
    >
      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Supplier</span>
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="order-supplier" disabled={isEdit}>
          <option value="">— select supplier —</option>
          {suppliers.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Date placed</span>
        <input type="date" value={datePlaced} onChange={(e) => setDatePlaced(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="order-date" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Status</span>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="order-status-select">
          {Object.keys(ORDER_STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>

      {!isEdit && (
        <div className="sm:col-span-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1.5">Line items</div>
          <div className="space-y-1.5" data-testid="order-items">
            {items.map((it, i) => {
              const m = materials.find((x) => x.id === it.material_id);
              return (
                <div key={i} className="grid grid-cols-[1fr_90px_auto] gap-2 items-center">
                  <select value={it.material_id} onChange={(e) => updateLine(i, "material_id", e.target.value)}
                    className="input-swiss w-full px-3 py-2 rounded-sm text-sm" data-testid={`order-item-material-${i}`}>
                    <option value="">— pick a material —</option>
                    {materials.filter((x) => x.active).map((mat) => (
                      <option key={mat.id} value={mat.id}>
                        {mat.category} · {mat.name}{mat.size ? ` · ${mat.size}` : ""}{mat.unit_label ? ` (${mat.unit_label})` : ""}
                      </option>
                    ))}
                  </select>
                  <input type="number" min="1" step="1" value={it.qty}
                    onChange={(e) => updateLine(i, "qty", e.target.value)}
                    className="input-swiss px-2 py-2 rounded-sm text-sm font-mono" data-testid={`order-item-qty-${i}`} />
                  <button type="button" onClick={() => removeLine(i)}
                    disabled={items.length === 1}
                    className="p-2 rounded-sm hover:bg-[#FEE2E2] disabled:opacity-30" aria-label="Remove line">
                    <Trash2 size={13} />
                  </button>
                  {m && (
                    <div className="col-span-3 -mt-1 text-[10px] font-mono text-[color:var(--ups-subtext)]">
                      Current stock: {m.stock_qty} · Last price: {fmtUSD(m.price_per_unit)} per {m.unit_label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button type="button" onClick={addLine} className="mt-2 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1 hover:underline" data-testid="add-line-item">
            <Plus size={11} /> Add line item
          </button>
        </div>
      )}

      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Total order amount ($)</span>
        <input type="number" min="0" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono" data-testid="order-total" />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Notes</span>
        <input value={notes} onChange={(e) => setNotes(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" placeholder="PO #, ship-by, etc." />
      </label>

      <div className="sm:col-span-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-2 text-[12px] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>
          <X size={12} className="inline mr-1" />Cancel
        </button>
        <button type="submit" disabled={busy}
          className="btn-brutal px-3 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em]" data-testid="order-save">
          {busy ? <Loader2 size={12} className="animate-spin" /> : (isEdit ? "Update Order" : "Place Order")}
        </button>
      </div>
    </form>
  );
};

const InventoryOrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  // Default to "Placed" only — Received orders are hidden unless the rep flips
  // the status filter (per user request).
  const [filter, setFilter] = useState({ status: "Placed", supplier_id: "", from_date: "", to_date: "" });

  const reload = async () => {
    setLoading(true);
    try {
      // Pull ALL orders so we can compute MTD/QTD/YTD totals from the full
      // history, then apply the visible filters client-side.
      const [o, s, m] = await Promise.all([
        inventoryListOrders({}),
        inventoryListSuppliers(),
        inventoryListMaterials(),
      ]);
      setOrders(o);
      setSuppliers(s);
      setMaterials(m);
    } catch (e) {
      toast.error(parseApiError(e, "Load failed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      if (editing?.id) {
        await inventoryUpdateOrder(editing.id, payload);
        toast.success("Updated");
      } else {
        const o = await inventoryCreateOrder(payload);
        toast.success(`Order ${o.order_number} placed`);
      }
      setCreating(false);
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(parseApiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`Delete ${o.order_number}? This cannot be undone.`)) return;
    try { await inventoryDeleteOrder(o.id); toast.success("Deleted"); await reload(); }
    catch (e) { toast.error("Delete failed"); }
  };

  /* ----------------------------------------------------------------
   * MTD / QTD / YTD spend — calendar buckets on date_placed; excludes
   * Cancelled. Returned in a single useMemo so the strip stays cheap.
   * --------------------------------------------------------------*/
  const spendStats = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const qStartMonth = Math.floor(m / 3) * 3;
    const monthStart = new Date(y, m, 1).getTime();
    const quarterStart = new Date(y, qStartMonth, 1).getTime();
    const yearStart = new Date(y, 0, 1).getTime();
    let mtd = 0, qtd = 0, ytd = 0;
    for (const o of orders) {
      if (o.status === "Cancelled") continue;
      const ts = o.date_placed ? new Date(o.date_placed).getTime() : 0;
      if (!ts) continue;
      const amt = Number(o.total_amount || 0);
      if (ts >= yearStart) ytd += amt;
      if (ts >= quarterStart) qtd += amt;
      if (ts >= monthStart) mtd += amt;
    }
    const quarterLabel = `Q${Math.floor(m / 3) + 1} ${y}`;
    return { mtd, qtd, ytd, monthLabel: now.toLocaleString("en-US", { month: "long", year: "numeric" }), quarterLabel, yearLabel: `${y}` };
  }, [orders]);

  /* Visible orders — client-side status / supplier / date filter */
  const visibleOrders = useMemo(() => {
    const fromTs = filter.from_date ? new Date(filter.from_date).getTime() : null;
    const toTs = filter.to_date ? new Date(filter.to_date + "T23:59:59").getTime() : null;
    return orders.filter((o) => {
      if (filter.status && o.status !== filter.status) return false;
      if (filter.supplier_id && o.supplier_id !== filter.supplier_id) return false;
      const ts = o.date_placed ? new Date(o.date_placed).getTime() : 0;
      if (fromTs && (!ts || ts < fromTs)) return false;
      if (toTs && (!ts || ts > toTs)) return false;
      return true;
    });
  }, [orders, filter]);

  const visibleTotalSpend = useMemo(
    () => visibleOrders.filter((o) => o.status !== "Cancelled").reduce((sum, o) => sum + (o.total_amount || 0), 0),
    [visibleOrders]
  );

  /* Quick-pick presets for the date filter */
  const datePresets = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const fmt = (d) => d.toISOString().slice(0, 10);
    return [
      { label: "This month", from: fmt(new Date(y, m, 1)), to: fmt(today) },
      { label: "Last month", from: fmt(new Date(y, m - 1, 1)), to: fmt(new Date(y, m, 0)) },
      { label: "This quarter", from: fmt(new Date(y, Math.floor(m / 3) * 3, 1)), to: fmt(today) },
      { label: "This year", from: fmt(new Date(y, 0, 1)), to: fmt(today) },
      { label: "All time", from: "", to: "" },
    ];
  };

  if (loading) return <div className="p-10 text-[color:var(--ups-subtext)] flex items-center"><Loader2 className="animate-spin mr-2" size={16} />Loading orders…</div>;

  const showForm = creating || editing;

  return (
    <div className="space-y-4" data-testid="inventory-orders-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl font-bold flex items-center gap-2"><ClipboardList size={20} /> Inventory Orders</h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
            Track every purchase order placed with your suppliers. Stock is independent — press <span className="font-mono">+</span> on the Materials tab when shipments arrive.
          </p>
        </div>
        {!showForm && (
          <button onClick={() => { setCreating(true); setEditing(null); }}
            className="btn-brutal px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" data-testid="new-order-btn">
            <Plus size={12} /> New Order
          </button>
        )}
      </div>

      {/* MTD / QTD / YTD Spend strip — computed from ALL orders (Cancelled excluded) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="spend-stats">
        <div className="card-swiss p-3 rounded-sm" data-testid="stat-mtd">
          <div className="overline flex items-center gap-1.5"><TrendingUp size={11} /> MTD · {spendStats.monthLabel}</div>
          <div className="font-display text-2xl font-black mt-0.5" style={{ color: "var(--ups-accent)" }}>{fmtUSD(spendStats.mtd)}</div>
        </div>
        <div className="card-swiss p-3 rounded-sm" data-testid="stat-qtd">
          <div className="overline flex items-center gap-1.5"><TrendingUp size={11} /> QTD · {spendStats.quarterLabel}</div>
          <div className="font-display text-2xl font-black mt-0.5" style={{ color: "var(--ups-accent)" }}>{fmtUSD(spendStats.qtd)}</div>
        </div>
        <div className="card-swiss p-3 rounded-sm" data-testid="stat-ytd">
          <div className="overline flex items-center gap-1.5"><TrendingUp size={11} /> YTD · {spendStats.yearLabel}</div>
          <div className="font-display text-2xl font-black mt-0.5" style={{ color: "var(--ups-accent)" }}>{fmtUSD(spendStats.ytd)}</div>
        </div>
      </div>

      {/* Visible-list summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="card-swiss p-3 rounded-sm">
          <div className="overline">Visible orders</div>
          <div className="font-display text-2xl font-black mt-0.5">{visibleOrders.filter((o) => o.status !== "Cancelled").length}</div>
        </div>
        <div className="card-swiss p-3 rounded-sm">
          <div className="overline">Visible spend</div>
          <div className="font-display text-2xl font-black mt-0.5">{fmtUSD(visibleTotalSpend)}</div>
        </div>
        <div className="card-swiss p-3 rounded-sm">
          <div className="overline">Suppliers used</div>
          <div className="font-display text-2xl font-black mt-0.5">{new Set(visibleOrders.map((o) => o.supplier_id).filter(Boolean)).size}</div>
        </div>
      </div>

      {showForm && (
        <OrderForm
          initial={editing}
          suppliers={suppliers}
          materials={materials}
          busy={busy}
          onSave={handleSave}
          onCancel={() => { setCreating(false); setEditing(null); }}
        />
      )}

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap p-3 rounded-sm" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
          <Filter size={14} className="text-[color:var(--ups-subtext)]" />
          <select value={filter.status} onChange={(e) => setFilter((p) => ({ ...p, status: e.target.value }))}
            className="input-swiss px-2 py-1.5 rounded-sm text-[12px] font-bold" data-testid="order-filter-status">
            <option value="Placed">Placed (default)</option>
            <option value="Received">Received</option>
            <option value="Cancelled">Cancelled</option>
            <option value="">All statuses</option>
          </select>
          <select value={filter.supplier_id} onChange={(e) => setFilter((p) => ({ ...p, supplier_id: e.target.value }))}
            className="input-swiss px-2 py-1.5 rounded-sm text-[12px]" data-testid="order-filter-supplier">
            <option value="">All suppliers</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {(filter.status !== "Placed" || filter.supplier_id || filter.from_date || filter.to_date) && (
            <button
              onClick={() => setFilter({ status: "Placed", supplier_id: "", from_date: "", to_date: "" })}
              className="ml-auto text-[10px] font-bold uppercase tracking-[0.12em] flex items-center gap-1 hover:opacity-70"
              data-testid="order-filter-reset"
            >
              <X size={11} /> Reset filters
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap p-3 rounded-sm" style={{ background: "#FFF8E1", border: "1px solid #FFB500" }}>
          <Calendar size={14} />
          <span className="text-[11px] font-bold uppercase tracking-[0.14em]">Date placed</span>
          <input
            type="date"
            value={filter.from_date}
            onChange={(e) => setFilter((p) => ({ ...p, from_date: e.target.value }))}
            className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
            data-testid="order-filter-date-from"
          />
          <span className="text-[11px]">→</span>
          <input
            type="date"
            value={filter.to_date}
            onChange={(e) => setFilter((p) => ({ ...p, to_date: e.target.value }))}
            className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
            data-testid="order-filter-date-to"
          />
          <div className="flex items-center gap-1 ml-2 flex-wrap">
            {datePresets().map((p) => (
              <button
                key={p.label}
                onClick={() => setFilter((prev) => ({ ...prev, from_date: p.from, to_date: p.to }))}
                className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm hover:bg-white"
                style={{ border: "1px solid var(--ups-border)" }}
                data-testid={`order-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Orders table */}
      {visibleOrders.length === 0 ? (
        <div className="p-10 text-center text-[color:var(--ups-subtext)] rounded-sm" style={{ border: "1px dashed var(--ups-border)", background: "#fff" }} data-testid="orders-empty">
          {orders.length === 0
            ? <>No inventory orders yet — click <strong>New Order</strong> to log your first purchase.</>
            : <>No orders match the current filters. <button onClick={() => setFilter({ status: "Placed", supplier_id: "", from_date: "", to_date: "" })} className="underline font-bold">Reset filters</button> to see Placed orders.</>}
        </div>
      ) : (
        <div className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }}>
          <table className="w-full text-[12px]">
            <thead style={{ background: "var(--ups-muted)" }}>
              <tr className="text-left">
                {["Order #", "Supplier", "Items", "Total", "Date", "Status", ""].map((h, i) => (
                  <th key={`${h}-${i}`} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em]"
                    style={{ textAlign: ["Total"].includes(h) ? "right" : "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((o) => (
                <tr key={o.id} className="border-t" style={{ borderColor: "var(--ups-border)" }} data-testid={`order-row-${o.id}`}>
                  <td className="px-3 py-2 font-mono font-bold text-[12px]">{o.order_number}</td>
                  <td className="px-3 py-2">{o.supplier_name || <em className="text-[color:var(--ups-subtext)]">—</em>}</td>
                  <td className="px-3 py-2 max-w-[280px]">
                    <div className="font-medium">{o.items.length} line{o.items.length === 1 ? "" : "s"} · {o.items.reduce((s, it) => s + (it.qty || 0), 0)} units</div>
                    <div className="text-[10px] text-[color:var(--ups-subtext)] truncate" title={o.items.map((it) => `${it.qty}× ${it.material_name}`).join(", ")}>
                      {o.items.map((it) => `${it.qty}× ${it.material_name}`).join(", ") || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono font-bold text-right" style={{ color: "var(--ups-accent)" }}>{fmtUSD(o.total_amount)}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-[color:var(--ups-subtext)] whitespace-nowrap">
                    <Calendar size={10} className="inline mr-1" />{fmtDate(o.date_placed)}
                  </td>
                  <td className="px-3 py-2"><StatusPill status={o.status} /></td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => { setEditing(o); setCreating(false); }} className="p-1.5 rounded-sm hover:bg-[color:var(--ups-muted)]" aria-label="Edit"><Pencil size={13} /></button>
                    <button onClick={() => handleDelete(o)} className="p-1.5 rounded-sm hover:bg-[#FEE2E2] ml-1" aria-label="Delete"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InventoryOrdersTab;
